import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  MapPin,
  Mail,
  ArrowUp,
  ExternalLink,
} from "lucide-react";
import { contactData } from "../data/mock";
import LogoImg from "../assets/android-chrome-512x512.png";

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavClick = (id) => {
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <footer className="bg-slate-950 relative overflow-hidden pt-16">
      {/* 🔹 Premium Border Top */}
      <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-amber-500 to-transparent opacity-50" />
      <div className="max-w-7xl mx-auto px-6 py-20 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
          {/* BRAND SECTION */}
          <div className="space-y-6">
            <div
              className="flex items-center gap-4 group cursor-pointer"
              onClick={() => navigate("/")}
            >
              <div className="w-14 h-14 bg-white/5 p-1.5 rounded-2xl backdrop-blur-sm border border-white/10 transition-transform group-hover:scale-110 duration-500">
                <img
                  src={LogoImg}
                  alt="Bahubali Cabs Udaipur"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h3 className="text-white font-black text-xl tracking-tighter italic">
                  BAHUBALI <span className="text-amber-500">CABS</span>
                </h3>
                <p className="text-slate-500 text-[10px] font-bold tracking-[0.3em] uppercase">
                  Udaipur's Royal Ride
                </p>
              </div>
            </div>

            <p className="text-slate-400 text-sm leading-relaxed">
              Udaipur's most trusted premium cab service. Providing royal comfort and local expertise for local sightseeing, outstations, and airport transfers.
            </p>

            <div className="flex flex-col gap-1.5">
              <span className="text-slate-550 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                Founding Owner
              </span>
              <p className="text-lg font-black text-amber-500">
                Hemant Bhat
              </p>
            </div>

            {/* Social Icons */}
            <div className="flex items-center gap-3 pt-2">
              <FooterSocialLink
                href={`tel:${contactData.phone}`}
                icon={<Phone />}
                color="bg-blue-600"
              />
              <FooterSocialLink
                href={`https://wa.me/${contactData.whatsapp}`}
                icon={<MessageCircle />}
                color="bg-emerald-600"
                isExternal
              />
              <FooterSocialLink
                href={`mailto:${contactData.email}`}
                icon={<Mail />}
                color="bg-amber-600"
              />
            </div>
          </div>

          {/* QUICK LINKS */}
          <div className="space-y-6">
            <FooterHeading title="Navigation" />
            <ul className="space-y-3">
              {[
                { label: "Home", id: "home", path: "/" },
                { label: "Services", id: "services" },
                { label: "Routes", id: "routes" },
                { label: "Fleet", id: "fleet" },
                { label: "Udaipur Sightseeing", id: "udaipur-sightseeing", path: "/udaipur-sightseeing" },
                { label: "Gallery", id: "gallery", path: "/gallery" },
                { label: "About Us", id: "about-us", path: "/about-us" },
                { label: "Contact", id: "contact" }
              ].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => handleNavClick(link.id, link.path)}
                    className="text-slate-400 hover:text-amber-500 text-sm font-bold transition-all duration-350 capitalize flex items-center gap-2 group"
                  >
                    <div className="w-1 h-1 rounded-full bg-slate-800 group-hover:bg-amber-500 transition-colors" />
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* POPULAR ROUTES SECTION */}
          <div className="space-y-6">
            <FooterHeading title="Popular Routes" />
            <ul className="space-y-3.5">
              {[
                { label: "Udaipur → Mount Abu", slug: "udaipur-to-mount-abu" },
                { label: "Udaipur → Jaipur", slug: "udaipur-to-jaipur" },
                { label: "Udaipur → Jodhpur", slug: "udaipur-to-jodhpur" },
                { label: "Udaipur → Jaisalmer", slug: "udaipur-to-jaisalmer" },
                { label: "Udaipur → Nathdwara", slug: "udaipur-to-nathdwara" }
              ].map((route) => (
                <li key={route.slug}>
                  <Link
                    to={`/routes/${route.slug}`}
                    className="text-slate-400 hover:text-white text-sm font-semibold leading-tight block group transition-colors"
                  >
                    <span className="text-amber-500 mr-1.5 opacity-70 group-hover:opacity-100 transition-opacity">
                      »
                    </span>
                    {route.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* CONTACT INFO & SEALS */}
          <div className="space-y-6">
            <FooterHeading title="Contact Info" />
            <div className="space-y-4 text-sm text-slate-400">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                <span className="text-slate-300">{contactData.address}</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-amber-500 shrink-0" />
                <a href={`tel:${contactData.phone}`} className="text-slate-200 hover:text-amber-500 transition-colors font-semibold">
                  {contactData.phone}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-amber-500 shrink-0" />
                <a href={`mailto:${contactData.email}`} className="text-slate-200 hover:text-amber-500 transition-colors font-semibold">
                  {contactData.email}
                </a>
              </div>
            </div>

            {/* Rajasthan Tourism badge */}
            <div className="mt-8 p-4 rounded-2xl bg-white/[0.02] border border-white/5 text-center">
              <p className="text-[10px] text-amber-500 font-black uppercase tracking-[0.25em] mb-1">Rajasthan Tourism</p>
              <p className="text-white text-xs font-bold">Authorized Cab Partner</p>
            </div>
          </div>
        </div>

        {/* 🔹 Final Copyright Bar */}
        <div className="mt-20 pt-10 border-t border-slate-900/50 flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left">
          <div className="space-y-2">
            <p className="text-slate-500 text-sm font-medium tracking-wide">
              © 2026{" "}
              <span className="text-white font-bold">
                BAHUBALI CABS UDAIPUR
              </span>
              . Built with Precision.
            </p>
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 md:gap-4 text-[10px] text-slate-500 font-bold uppercase tracking-widest">
              <Link to="/privacy-policy" className="hover:text-amber-500 transition-colors">Privacy Policy</Link>
              <div className="w-1 h-1 bg-slate-800 rounded-full" />
              <Link to="/terms-and-conditions" className="hover:text-amber-500 transition-colors">Terms of Service</Link>
              <div className="w-1 h-1 bg-slate-800 rounded-full" />
              <Link to="/disclaimer" className="hover:text-amber-500 transition-colors">Disclaimer</Link>
              <div className="w-1 h-1 bg-slate-800 rounded-full" />
              <Link to="/support" className="hover:text-amber-500 transition-colors">Support & FAQs</Link>
            </div>
          </div>

          <a
            href="https://parshwebcraft.in"
            target="_blank"
            className="flex items-center gap-2 text-slate-400 hover:text-amber-400 transition-all font-bold group"
          >
            Design by{" "}
            <span className="bg-amber-500 text-slate-950 px-2 py-0.5 rounded-md group-hover:rotate-2 transition-transform">
              ParshWebCraft
            </span>
            <ExternalLink size={14} />
          </a>
        </div>
      </div>

      {/* 🔹 Back to Top Button */}
      <button
        onClick={scrollToTop}
        className="absolute bottom-10 right-10 w-14 h-14 bg-amber-500 text-slate-950 rounded-2xl flex items-center justify-center shadow-2xl shadow-amber-500/40 hover:-translate-y-2 transition-all duration-300"
      >
        <ArrowUp size={28} />
      </button>
    </footer>
  );
};

/* Helper Components for Audit Cleanliness */
const FooterHeading = ({ title }) => (
  <h4 className="text-white font-black text-sm uppercase tracking-[0.2em] border-l-4 border-amber-500 pl-4 mb-8">
    {title}
  </h4>
);

const FooterSocialLink = ({ href, icon, color, isExternal }) => (
  <a
    href={href}
    target={isExternal ? "_blank" : "_self"}
    className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center text-white shadow-lg hover:shadow-inherit hover:-translate-y-1.5 transition-all duration-300`}
  >
    {React.cloneElement(icon, { size: 20 })}
  </a>
);

export default Footer;
